import { useEffect } from "react";
import {
  Routes,
  Route,
  useNavigationType,
  useLocation,
} from "react-router-dom";
import LandingPage from "./pages/LandingPage";
import Tags from "./components/Tags";
import SignUpForm from "./components/SignUpForm";
import SignInForm from "./components/SignInForm";
import ForgotPasswordForm from "./components/ForgotPasswordForm";
import ResetPasswordForm from "./components/ResetPasswordForm";
import GoogleOAuthCallback from "./components/GoogleOAuthCallback";
import Dashboard from "./pages/Dashboard";
import Brokers from "./pages/Brokers";
import Strategies from "./pages/Strategies";
import PublicStrategies from "./pages/PublicStrategies";
import Charts from "./pages/Charts";
import Backtest from "./pages/Backtest";
import BacktestResults from "./pages/BacktestResults";
import LiveTrading from "./pages/LiveTrading";
import StrategyBuilderDemo from "./pages/StrategyBuilderDemo";
import AIChat from "./pages/AIChat";
import StrategyChat from "./pages/StrategyChat";
import AiUsage from "./pages/AiUsage";
import AiWallet from "./pages/AiWallet";
import { AuthProvider } from './contexts/AuthContext';
import { SchemaProvider } from './contexts/SchemaContext';
import WaitlistPage from "./pages/WaitlistPage";
import PublicInfoPage from "./pages/PublicInfoPage";
import ProtectedRoute from "./components/ProtectedRoute";
import GuestRoute from "./components/GuestRoute";

import ChartV2Page from "./pages/ChartV2Page";
import ChartV3Page from "./pages/ChartV3Page";

function App() {
  const action = useNavigationType();
  const location = useLocation();
  const pathname = location.pathname;

  useEffect(() => {
    if (action !== "POP") {
      window.scrollTo(0, 0);
    }
  }, [action, pathname]);

  useEffect(() => {
    let title = "";
    let metaDescription = "";

    switch (pathname) {
      case "/":
        title = "StockAlgoTrader";
        metaDescription = "Stock Algorithm Trading Platform";
        break;
      case "/signup":
        title = "Sign Up - StockAlgoTrader";
        metaDescription = "Create your account";
        break;
      case "/signin":
        title = "Sign In - StockAlgoTrader";
        metaDescription = "Sign in to your account";
        break;
      case "/forgot-password":
        title = "Forgot Password - StockAlgoTrader";
        metaDescription = "Reset your password";
        break;
      case "/reset-password":
        title = "Reset Password - StockAlgoTrader";
        metaDescription = "Create a new password";
        break;
      case "/dashboard":
        title = "Dashboard - StockAlgoTrader";
        metaDescription = "Your trading dashboard";
        break;
      case "/dashboard/ai-chat":
        title = "AI Chat - StockAlgoTrader";
        metaDescription = "Chat with SAT AI Agent";
        break;
      case "/dashboard/ai-usage":
        title = "AI Usage - StockAlgoTrader";
        metaDescription = "AI token usage and billing";
        break;
      default:
        break;
    }

    if (title) {
      document.title = title;
    }

    if (metaDescription) {
      const metaDescriptionTag = document.querySelector(
        'head > meta[name="description"]'
      );
      if (metaDescriptionTag) {
        metaDescriptionTag.content = metaDescription;
      }
    }
  }, [pathname]);

  return (
    <AuthProvider>
      <SchemaProvider>
        <Routes>
          <Route path="/" element={<LandingPage />} />
          <Route path="/tags" element={<Tags />} />
          <Route path="/signup" element={<GuestRoute><SignUpForm /></GuestRoute>} />
          <Route path="/signin" element={<GuestRoute><SignInForm /></GuestRoute>} />
          <Route path="/forgot-password" element={<GuestRoute><ForgotPasswordForm /></GuestRoute>} />
          <Route path="/reset-password" element={<GuestRoute><ResetPasswordForm /></GuestRoute>} />
          <Route path="/auth/google/callback" element={<GoogleOAuthCallback />} />
          <Route path="/waitlist" element={<WaitlistPage />} />
          <Route path="/info/:pageId" element={<PublicInfoPage />} />
          <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
          <Route path="/dashboard/brokers" element={<ProtectedRoute><Brokers /></ProtectedRoute>} />
          <Route path="/dashboard/strategies" element={<ProtectedRoute><Strategies initialTab="list" /></ProtectedRoute>} />
          <Route path="/dashboard/strategies/public" element={<ProtectedRoute><PublicStrategies /></ProtectedRoute>} />
          <Route path="/dashboard/strategies/new" element={<ProtectedRoute><Strategies initialTab="create" /></ProtectedRoute>} />
          <Route path="/dashboard/strategies/create" element={<ProtectedRoute><Strategies initialTab="create" /></ProtectedRoute>} />
          <Route path="/dashboard/strategies/list" element={<ProtectedRoute><Strategies initialTab="list" /></ProtectedRoute>} />
          <Route path="/dashboard/charts" element={<ProtectedRoute><Charts /></ProtectedRoute>} />
          <Route path="/dashboard/charts/v2" element={<ProtectedRoute><ChartV2Page /></ProtectedRoute>} />
          <Route path="/dashboard/charts/v3" element={<ProtectedRoute><ChartV3Page /></ProtectedRoute>} />
          <Route path="/dashboard/backtest" element={<ProtectedRoute><Backtest /></ProtectedRoute>} />
          <Route path="/dashboard/backtest/results/:id" element={<ProtectedRoute><BacktestResults /></ProtectedRoute>} />
          <Route path="/dashboard/live-trading" element={<ProtectedRoute><LiveTrading /></ProtectedRoute>} />
          <Route path="/dashboard/ai-chat" element={<ProtectedRoute><AIChat /></ProtectedRoute>} />
          <Route path="/dashboard/strategy-chat" element={<ProtectedRoute><StrategyChat /></ProtectedRoute>} />
          <Route path="/dashboard/ai-usage" element={<ProtectedRoute><AiUsage /></ProtectedRoute>} />
          <Route path="/dashboard/ai-wallet" element={<ProtectedRoute><AiWallet /></ProtectedRoute>} />
          <Route path="/strategy-builder-demo" element={<StrategyBuilderDemo />} />
        </Routes>
      </SchemaProvider>
    </AuthProvider>
  );
}

export default App;
